import config from "../config/api";
import { PREDEFINED_SYMBOLS } from "../constants/symbols";
import { buildDefaultLogoUrlForSymbol } from "./symbolBranding";

export interface StockUniverseEntry {
  name: string;
  rank: number;
  sectors: string[];
  assetType?: "stock" | "etf" | "future";
  logoUrl?: string;
}

interface StockUniverseResponse {
  count: number;
  stocks: Record<string, RawStockUniverseEntry>;
  sector_to_stocks?: Record<string, string[]>;
  industry_to_stocks?: Record<string, string[]>;
}

interface RawStockUniverseEntry {
  name?: unknown;
  rank?: unknown;
  sectors?: unknown;
  assetType?: unknown;
  logoUrl?: unknown;
  logo_url?: unknown;
}

export const FALLBACK_STOCK_UNIVERSE: Record<string, StockUniverseEntry> = Object.fromEntries(
  Object.entries(PREDEFINED_SYMBOLS).map(([symbol, name], index) => [
    symbol,
    {
      name,
      rank: index + 1,
      sectors: [],
      assetType: "stock",
      logoUrl: buildDefaultLogoUrlForSymbol(symbol) ?? undefined,
    },
  ])
);

export interface StockUniverseData {
  stocks: Record<string, StockUniverseEntry>;
  sectorToStocks: Record<string, string[]>;
  industryToStocks: Record<string, string[]>;
}

const normalizeSymbol = (rawSymbol: string): string => rawSymbol.trim().toUpperCase();

const normalizeCategoryMap = (
  rawMap: Record<string, string[]> | undefined,
  stocks: Record<string, StockUniverseEntry>
): Record<string, string[]> => {
  if (!rawMap || typeof rawMap !== "object") {
    return {};
  }

  const normalized = Object.entries(rawMap).reduce<Record<string, string[]>>((acc, [category, symbols]) => {
    const normalizedCategory = category.trim();
    if (!normalizedCategory || !Array.isArray(symbols)) {
      return acc;
    }

    const uniqueSymbols = Array.from(
      new Set(
        symbols
          .filter((value): value is string => typeof value === "string")
          .map(normalizeSymbol)
          .filter((symbol) => symbol in stocks)
      )
    );

    if (uniqueSymbols.length === 0) {
      return acc;
    }

    uniqueSymbols.sort((left, right) => {
      const rankDiff = (stocks[left]?.rank ?? Number.MAX_SAFE_INTEGER) - (stocks[right]?.rank ?? Number.MAX_SAFE_INTEGER);
      return rankDiff !== 0 ? rankDiff : left.localeCompare(right);
    });

    acc[normalizedCategory] = uniqueSymbols;
    return acc;
  }, {});

  return Object.fromEntries(Object.entries(normalized).sort(([left], [right]) => left.localeCompare(right)));
};

const deriveCategoryMap = (
  stocks: Record<string, StockUniverseEntry>,
  categoryIndex: number
): Record<string, string[]> => {
  const derived: Record<string, string[]> = {};

  for (const [symbol, entry] of Object.entries(stocks)) {
    const category = entry.sectors[categoryIndex]?.trim();
    if (!category) {
      continue;
    }

    if (!derived[category]) {
      derived[category] = [];
    }
    derived[category].push(symbol);
  }

  return normalizeCategoryMap(derived, stocks);
};

const resolveCategoryMap = (
  rawMap: Record<string, string[]> | undefined,
  stocks: Record<string, StockUniverseEntry>,
  categoryIndex: number
): Record<string, string[]> => {
  const normalized = normalizeCategoryMap(rawMap, stocks);
  if (Object.keys(normalized).length > 0) {
    return normalized;
  }
  return deriveCategoryMap(stocks, categoryIndex);
};

export const FALLBACK_STOCK_UNIVERSE_DATA: StockUniverseData = {
  stocks: FALLBACK_STOCK_UNIVERSE,
  sectorToStocks: deriveCategoryMap(FALLBACK_STOCK_UNIVERSE, 0),
  industryToStocks: deriveCategoryMap(FALLBACK_STOCK_UNIVERSE, 1),
};

let stockUniversePromise: Promise<StockUniverseData> | null = null;

// Single source of truth: the supported_tickers collection, served by rust-backend. Every symbol
// here is one the IBKR feed can resolve, futures included. Built from config because in production
// the reverse proxy sends /api/ to the chatbot app; rust-backend sits behind /trading/.
const SUPPORTED_TICKERS_PATH = `${config.tradingApiUrl}/trading/market/tickers`;

const isValidUniverseResponse = (value: unknown): value is StockUniverseResponse => {
  if (typeof value !== "object" || value === null) return false;
  const payload = value as { stocks?: unknown };
  return typeof payload.stocks === "object" && payload.stocks !== null;
};

const sanitizeLogoUrl = (entry: RawStockUniverseEntry): string | undefined => {
  const candidate = typeof entry.logoUrl === "string"
    ? entry.logoUrl.trim()
    : typeof entry.logo_url === "string"
      ? entry.logo_url.trim()
      : "";
  return candidate || undefined;
};

const withAssetType = (
  stocks: Record<string, RawStockUniverseEntry>,
  assetType: "stock" | "etf" | "future"
): Record<string, StockUniverseEntry> => {
  const next: Record<string, StockUniverseEntry> = {};

  for (const [symbol, entry] of Object.entries(stocks)) {
    const normalized = normalizeSymbol(symbol);
    if (!normalized) continue;

    const normalizedSectors = Array.isArray(entry.sectors)
      ? Array.from(
        new Set(
          entry.sectors
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
        )
      )
      : [];

    const normalizedName = typeof entry.name === "string" && entry.name.trim().length > 0
      ? entry.name.trim()
      : normalized;

    const explicitLogoUrl = sanitizeLogoUrl(entry);

    next[normalized] = {
      name: normalizedName,
      rank: typeof entry.rank === "number" && Number.isFinite(entry.rank) ? entry.rank : Number.MAX_SAFE_INTEGER,
      sectors: normalizedSectors,
      assetType,
      logoUrl: explicitLogoUrl ?? buildDefaultLogoUrlForSymbol(normalized) ?? undefined,
    };
  }

  return next;
};

const normalizeStockUniverse = (
  rows: Record<string, RawStockUniverseEntry>
): Record<string, StockUniverseEntry> => {
  const next: Record<string, StockUniverseEntry> = {};

  for (const [symbol, entry] of Object.entries(rows)) {
    const assetType =
      entry.assetType === "etf" || entry.assetType === "future" ? entry.assetType : "stock";
    Object.assign(next, withAssetType({ [symbol]: entry }, assetType));
  }

  return next;
};

const appendMissingPredefinedSymbols = (
  stocks: Record<string, StockUniverseEntry>
): Record<string, StockUniverseEntry> => {
  const next = { ...stocks };
  let nextRank =
    Object.values(next).reduce((maxRank, entry) => {
      if (!Number.isFinite(entry.rank) || entry.rank >= Number.MAX_SAFE_INTEGER) {
        return maxRank;
      }
      return Math.max(maxRank, entry.rank);
    }, 0) + 1;

  for (const [symbol, name] of Object.entries(PREDEFINED_SYMBOLS)) {
    const normalized = normalizeSymbol(symbol);
    if (!normalized || normalized in next) {
      continue;
    }

    next[normalized] = {
      name,
      rank: nextRank++,
      sectors: [],
      assetType: "stock",
      logoUrl: buildDefaultLogoUrlForSymbol(normalized) ?? undefined,
    };
  }

  return next;
};

const fetchUniverse = async (path: string): Promise<StockUniverseResponse | null> => {
  try {
    const response = await fetch(path);
    if (!response.ok) return null;

    const payload = await response.json();
    if (!isValidUniverseResponse(payload)) {
      return null;
    }
    return payload;
  } catch {
    return null;
  }
};

export async function loadStockUniverse(): Promise<StockUniverseData> {
  if (!stockUniversePromise) {
    stockUniversePromise = fetchUniverse(SUPPORTED_TICKERS_PATH)
      .then((payload) => {
        const supported = payload?.stocks ?? FALLBACK_STOCK_UNIVERSE;
        const normalized = normalizeStockUniverse(supported);
        const mergedWithPredefined = appendMissingPredefinedSymbols(normalized);

        const sectorToStocks = deriveCategoryMap(mergedWithPredefined, 0);
        const industryToStocks = deriveCategoryMap(mergedWithPredefined, 1);

        return {
          stocks: mergedWithPredefined,
          sectorToStocks:
            Object.keys(sectorToStocks).length > 0
              ? sectorToStocks
              : resolveCategoryMap(payload?.sector_to_stocks, mergedWithPredefined, 0),
          industryToStocks:
            Object.keys(industryToStocks).length > 0
              ? industryToStocks
              : resolveCategoryMap(payload?.industry_to_stocks, mergedWithPredefined, 1),
        };
      })
      .catch((error) => {
        stockUniversePromise = null;
        throw error;
      });
  }

  return stockUniversePromise;
}
